import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Phone, Mail, X, Save } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CrmShell } from "./CrmShell";
import { SourceBadge } from "./SourceBadge";
import { ukDate } from "./crmShared";
import { AInput, ATextarea, ASelect } from "@/components/admin/AdminInputs";

export const LEAD_STATUSES = ["New", "Contact attempted", "Contacted", "Qualified", "Viewing required",
  "Offer made", "Negotiating", "Under offer", "Completed", "Not suitable", "Nurture", "Closed"];
export const LEAD_PRIORITIES = ["Hot", "Warm", "Cold"];
const PROPERTY_TYPES = ["", "Detached", "Semi-detached", "Terraced", "Flat / Apartment", "Bungalow", "Maisonette", "Land / Plot", "Other"];
const TIMELINES = ["", "Immediately", "Within 1 month", "1–3 months", "3–6 months", "Just exploring"];

const priBadge = (p) => p === "Hot" ? "bg-orange-100 text-orange-800" : p === "Warm" ? "bg-amber-100 text-amber-800" : "bg-slate-200 text-slate-600";
const statBadge = (s) => s === "New" ? "bg-blue-100 text-blue-800" : s === "Completed" ? "bg-emerald-100 text-emerald-800" : (s === "Not suitable" || s === "Closed") ? "bg-slate-200 text-slate-600" : "bg-indigo-100 text-indigo-800";

const EMPTY = { full_name: "", email: "", phone: "", address: "", postcode: "", property_type: "", bedrooms: "",
  condition: "", occupancy: "", asking_price: "", price_expectation: "", reason: "", timeline: "", details: "", source: "Manual" };

function AddVendorModal({ onClose, onCreated }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.full_name.trim()) return toast.error("Name is required");
    setBusy(true);
    try {
      const { data } = await api.post("/crm/vendor-leads", form);
      toast.success("Vendor lead added");
      onCreated(data.lead_id);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add vendor lead"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto py-8" data-testid="vendor-add-modal">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-[#0B241B] border border-white/10 rounded-xl p-5 mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-serif text-white">Add Vendor Lead</h3>
          <button onClick={onClose} data-testid="vendor-add-close"><X className="w-5 h-5 text-emerald-100/70" /></button>
        </div>
        <p className="text-xs text-emerald-100/50 mb-4">Manually captured leads are saved to the CRM but do <strong>not</strong> send new-lead email alerts.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <AInput label="Full name" value={form.full_name} onChange={set("full_name")} testId="vendor-add-name" />
          <AInput label="Phone" value={form.phone} onChange={set("phone")} testId="vendor-add-phone" />
          <AInput label="Email" value={form.email} onChange={set("email")} testId="vendor-add-email" />
          <AInput label="Postcode" value={form.postcode} onChange={set("postcode")} testId="vendor-add-postcode" />
          <AInput label="Address" value={form.address} onChange={set("address")} />
          <ASelect label="Property type" value={form.property_type} onChange={set("property_type")} options={PROPERTY_TYPES} />
          <AInput label="Bedrooms" value={form.bedrooms} onChange={set("bedrooms")} />
          <AInput label="Condition" value={form.condition} onChange={set("condition")} />
          <AInput label="Occupancy" value={form.occupancy} onChange={set("occupancy")} />
          <ASelect label="Timeline" value={form.timeline} onChange={set("timeline")} options={TIMELINES} />
          <AInput label="Asking price (£)" value={form.asking_price} onChange={set("asking_price")} />
          <AInput label="Price expectation (£)" value={form.price_expectation} onChange={set("price_expectation")} />
          <AInput label="Reason for selling" value={form.reason} onChange={set("reason")} />
          <AInput label="Lead source" value={form.source} onChange={set("source")} />
        </div>
        <div className="mt-3"><ATextarea label="Notes / details" value={form.details} onChange={set("details")} rows={3} /></div>
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="text-sm text-emerald-100/70 hover:text-white px-4 py-2">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="vendor-add-save" className="flex items-center gap-1.5 bg-brass hover:bg-brass-600 text-emerald-950 font-semibold rounded-full px-5 py-2 text-sm"><Save className="w-4 h-4" /> {busy ? "Saving…" : "Add lead"}</button>
        </div>
      </div>
    </div>
  );
}

export default function CrmVendorLeads() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [filters, setFilters] = useState({ q: "", status: "", priority: "" });
  const [showAdd, setShowAdd] = useState(false);

  const load = () => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    api.get("/crm/vendor-leads", { params }).then((r) => setList(r.data)).catch(() => toast.error("Could not load vendor leads"));
  };
  useEffect(load, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <CrmShell title="Vendor Leads">
      {showAdd && <AddVendorModal onClose={() => setShowAdd(false)} onCreated={(id) => navigate(`/crm/vendor-leads/${id}`)} />}
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]"><Search className="w-4 h-4 absolute left-3 top-2.5 text-emerald-100/40" />
          <input value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} placeholder="Search name, email, phone, postcode" data-testid="vendor-search" className="w-full bg-[#0F2E23] border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder:text-emerald-100/40" /></div>
        <select value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))} data-testid="vendor-filter-status" className="bg-[#0F2E23] border border-white/10 rounded-lg px-3 py-2 text-sm text-white"><option value="">All statuses</option>{LEAD_STATUSES.map((s) => <option key={s}>{s}</option>)}</select>
        <select value={filters.priority} onChange={(e) => setFilters((f) => ({ ...f, priority: e.target.value }))} data-testid="vendor-filter-priority" className="bg-[#0F2E23] border border-white/10 rounded-lg px-3 py-2 text-sm text-white"><option value="">Any priority</option>{LEAD_PRIORITIES.map((s) => <option key={s}>{s}</option>)}</select>
        <button onClick={() => setShowAdd(true)} data-testid="vendor-new" className="flex items-center gap-1.5 bg-brass hover:bg-brass-600 text-emerald-950 font-semibold rounded-full px-4 py-2 text-sm"><Plus className="w-4 h-4" /> Add Vendor Lead</button>
      </div>
      <div className="bg-[#0F2E23] rounded-xl border border-white/10 overflow-x-auto" data-testid="vendor-table">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-emerald-100/50 border-b border-white/10">
            <th className="py-2.5 px-3">Name</th>
            <th className="py-2.5 px-3 hidden sm:table-cell">Contact</th>
            <th className="py-2.5 px-3 hidden md:table-cell">Property</th>
            <th className="py-2.5 px-3 hidden lg:table-cell">Timeline</th>
            <th className="py-2.5 px-3">Priority</th>
            <th className="py-2.5 px-3">Status</th>
            <th className="py-2.5 px-3 hidden lg:table-cell">Received</th>
          </tr></thead>
          <tbody>{list.map((l) => {
            const pd = l.property_details || {};
            return (
            <tr key={l.lead_id} onClick={() => navigate(`/crm/vendor-leads/${l.lead_id}`)} data-testid={`vendor-row-${l.lead_id}`} className="border-b border-white/5 hover:bg-white/5 cursor-pointer">
              <td className="py-3 px-3"><div className="text-white font-medium">{l.full_name}</div><div className="text-xs text-emerald-100/50 flex items-center gap-1.5">{l.postcode || l.address || "—"} <SourceBadge source={l.original_source || l.source} testId={`vendor-row-source-${l.lead_id}`} /></div></td>
              <td className="py-3 px-3 hidden sm:table-cell" onClick={(e) => e.stopPropagation()}>
                <div className="flex flex-col gap-0.5">
                  {l.phone && <a href={`tel:${l.phone}`} data-testid={`vendor-tel-${l.lead_id}`} className="flex items-center gap-1 text-emerald-100/70 hover:text-brass text-xs"><Phone className="w-3 h-3" />{l.phone}</a>}
                  {l.email && <a href={`mailto:${l.email}`} data-testid={`vendor-mail-${l.lead_id}`} className="flex items-center gap-1 text-emerald-100/70 hover:text-brass text-xs"><Mail className="w-3 h-3" />{l.email}</a>}
                </div>
              </td>
              <td className="py-3 px-3 hidden md:table-cell text-emerald-100/70 text-xs">{[pd.property_type, pd.bedrooms].filter(Boolean).join(" · ") || "—"}</td>
              <td className="py-3 px-3 hidden lg:table-cell text-emerald-100/60 text-xs">{pd.timeline || "—"}</td>
              <td className="py-3 px-3"><span className={`text-[11px] px-2 py-0.5 rounded-full ${priBadge(l.priority)}`}>{l.priority}</span></td>
              <td className="py-3 px-3"><span className={`text-[11px] px-2 py-0.5 rounded-full ${statBadge(l.status)}`}>{l.status}</span></td>
              <td className="py-3 px-3 hidden lg:table-cell text-emerald-100/60 text-xs">{ukDate(l.created_at)}</td>
            </tr>);
          })}</tbody>
        </table>
        {list.length === 0 && <p className="text-emerald-100/50 text-sm py-8 text-center">No vendor leads match.</p>}
      </div>
    </CrmShell>
  );
}
